/**
 * 向 auth-service 查詢目前登入者，並回報三種狀態之一。
 *
 * 三態而非布林值：`loading` 必須與 `unauthenticated` 分開，否則首次渲染會在
 * 查詢還沒回來時先閃一下登入頁 —— 對已登入的使用者是明顯的畫面跳動。
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchSession, type AuthUser, type SessionStatus } from './session';
import { onSessionExpired } from './session-expiry';

export interface UseSessionReturn {
  readonly status: SessionStatus;
  readonly user: AuthUser | null;
  /** 查詢本身失敗（網路不通、CORS 被擋）。與「未登入」是不同的情況 */
  readonly error: Error | null;
  /** 重新查詢，例如從登入導回後想立刻更新畫面 */
  readonly refresh: () => void;
}

export function useSession(): UseSessionReturn {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  /* 回到 loading 的重設放在這裡而非 effect 裡：effect body 內同步 setState 會造成
     連鎖渲染（react-hooks/set-state-in-effect），而重設本來就是「使用者按了重試」
     這個事件的一部分，寫在事件處理器裡也更貼近實際因果 */
  const refresh = useCallback(() => {
    setStatus('loading');
    setError(null);
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    /* 元件在請求回來前卸載（或 refresh 觸發新一輪）時中止，避免對已卸載的元件 setState */
    const controller = new AbortController();

    fetchSession(controller.signal)
      .then((result) => {
        setUser(result);
        setStatus(result ? 'authenticated' : 'unauthenticated');
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        /* 查不到登入狀態時一律當作未登入 —— 讓使用者看到登入頁並可重試，
           比卡在無限 loading 好；真正的原因另外以 error 回報供畫面提示 */
        setError(cause instanceof Error ? cause : new Error(String(cause)));
        setUser(null);
        setStatus('unauthenticated');
      });

    return () => controller.abort();
  }, [attempt]);

  /*
   * 使用期間 session 過期（任何一支 apiFetch 收到 401）時，直接把狀態翻回未登入，
   * AuthGate 便會換上登入頁。不重新查詢 auth-service 再確認一次：401 已經是後端
   * 給的答案，再問一次只是多一趟必然同樣結果的請求，還會讓畫面多停在錯誤狀態一拍。
   */
  useEffect(
    () =>
      onSessionExpired(() => {
        setUser(null);
        setStatus('unauthenticated');
      }),
    [],
  );

  return { status, user, error, refresh };
}
